/**
 * Streaming band-limited resampler (windowed-sinc, Blackman window) for converting microphone
 * audio (typically 44.1 or 48 kHz) to the 16 kHz the phone model expects. Feed chunks of any size
 * with `push`; it keeps the filter history between calls.
 *
 * Kernels are precomputed for PHASES fractional positions (polyphase table), so each output sample
 * is one dot product: about 1-2 ms of CPU per second of audio.
 */

const PHASES = 256;

export class Resampler {
  private readonly ratio: number;
  private readonly taps: number;
  private readonly half: number;
  private readonly table: Float32Array;
  private buffer = new Float32Array(0);
  /** Position of the next output sample, in input samples relative to `buffer[0]`. */
  private position: number;

  readonly inputRate: number;
  readonly outputRate: number;

  constructor(inputRate: number, outputRate: number, halfTaps = 16) {
    this.inputRate = inputRate;
    this.outputRate = outputRate;
    this.ratio = inputRate / outputRate;
    // Low-pass at 90% of the output Nyquist frequency when downsampling.
    const cutoff = Math.min(1, 1 / this.ratio) * 0.9;
    this.half = Math.ceil(halfTaps * Math.max(1, this.ratio));
    this.taps = 2 * this.half;
    this.table = new Float32Array(PHASES * this.taps);
    for (let phase = 0; phase < PHASES; phase++) {
      const frac = phase / PHASES;
      let norm = 0;
      for (let j = 0; j < this.taps; j++) {
        const x = j - this.half + 1 - frac;
        const sinc = x === 0 ? 1 : Math.sin(Math.PI * cutoff * x) / (Math.PI * cutoff * x);
        const w =
          0.42 +
          0.5 * Math.cos((Math.PI * x) / this.half) +
          0.08 * Math.cos((2 * Math.PI * x) / this.half);
        const weight = sinc * Math.max(w, 0);
        this.table[phase * this.taps + j] = weight;
        norm += weight;
      }
      for (let j = 0; j < this.taps; j++)
        this.table[phase * this.taps + j] = (this.table[phase * this.taps + j] ?? 0) / norm;
    }
    this.position = this.half;
  }

  push(chunk: Float32Array): Float32Array {
    if (this.inputRate === this.outputRate) return chunk.slice();
    const merged = new Float32Array(this.buffer.length + chunk.length);
    merged.set(this.buffer);
    merged.set(chunk, this.buffer.length);

    const count = Math.max(0, Math.ceil((merged.length - this.half - this.position) / this.ratio));
    const out = new Float32Array(count);
    let n = 0;
    while (this.position + this.half < merged.length && n < count) {
      const center = Math.floor(this.position);
      const phase = Math.min(PHASES - 1, Math.round((this.position - center) * PHASES));
      const kernel = phase * this.taps;
      const start = center - this.half + 1;
      let sum = 0;
      for (let j = 0; j < this.taps; j++)
        sum += (merged[start + j] ?? 0) * (this.table[kernel + j] ?? 0);
      out[n++] = sum;
      this.position += this.ratio;
    }
    const keepFrom = Math.max(0, Math.floor(this.position) - this.half);
    this.buffer = merged.slice(keepFrom);
    this.position -= keepFrom;
    return out.subarray(0, n);
  }
}

/** One-shot convenience: resample a whole buffer. */
export function resample(input: Float32Array, inputRate: number, outputRate: number): Float32Array {
  const resampler = new Resampler(inputRate, outputRate);
  const head = resampler.push(input);
  const tail = resampler.push(new Float32Array(Math.ceil(inputRate / 50)));
  const expected = Math.round((input.length * outputRate) / inputRate);
  const out = new Float32Array(expected);
  out.set(head.subarray(0, expected));
  if (head.length < expected) out.set(tail.subarray(0, expected - head.length), head.length);
  return out;
}
